import {
  trackMetaViewContent,
  trackMetaAddToCart,
  trackMetaInitiateCheckout,
  trackMetaAddPaymentInfo,
  trackMetaPurchase,
  newMetaEventId,
} from './metaConversions';

const STORAGE_PENDING_PURCHASE = 'cervalle_gtm_pending_purchase';
const LS_PURCHASE_RECORD_PREFIX = 'cervalle_ecom_purchase_recorded_';

const EVENT_VIEW_ITEM = 'view_item';
const EVENT_SELECT_ITEM = 'select_item';
const EVENT_ADD_TO_CART = 'add_to_cart';
const EVENT_VIEW_CART = 'view_cart';
const EVENT_BEGIN_CHECKOUT = 'begin_checkout';
const EVENT_ADD_PAYMENT_INFO = 'add_payment_info';
const EVENT_PURCHASE = 'purchase';

export function setEcommerceCurrencyCode(currencyCode) {
  if (typeof window === 'undefined') return;
  window.__CERVALLE_ECOMMERCE_CURRENCY__ = normalizeCurrencyCode(currencyCode);
}

export function getEcommerceCurrencyCode() {
  if (typeof window !== 'undefined' && window.__CERVALLE_ECOMMERCE_CURRENCY__) {
    return normalizeCurrencyCode(window.__CERVALLE_ECOMMERCE_CURRENCY__);
  }
  return 'COP';
}

export function normalizeCurrencyCode(currency) {
  return String(currency || 'COP').toUpperCase();
}

function ensureDataLayer() {
  if (typeof window === 'undefined') return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

export function normalizeItems(items) {
  return (items || []).map((row) => ({
    item_id: String(row.item_id),
    item_name: String(row.item_name),
    price: Number(row.price),
    quantity: Number(row.quantity),
  }));
}

export function pushEcommerceEvent(eventName, ecommercePayload) {
  const dl = ensureDataLayer();
  if (!dl || !ecommercePayload || !ecommercePayload.items || ecommercePayload.items.length === 0) return;
  const currency = normalizeCurrencyCode(ecommercePayload.currency);
  const value = Number(ecommercePayload.value);
  const items = normalizeItems(ecommercePayload.items);
  dl.push({ ecommerce: null });
  const ecommerce = {
    currency,
    value,
    items,
  };
  if (ecommercePayload.transaction_id != null) {
    ecommerce.transaction_id = String(ecommercePayload.transaction_id);
  }
  dl.push({
    event: eventName,
    ecommerce,
  });
}

export function lineItemFromCartProduct(product) {
  if (!product) return null;
  const isGift = product.type === 'giftcard';
  const rawId = isGift ? product.id : (product.id != null && product.id !== '' ? product.id : product.plu);
  if (rawId == null || rawId === '') return null;
  const itemId = String(rawId);
  const itemName = isGift
    ? String(product.name?.nombre ?? product.nombre ?? product.title ?? '').trim()
    : String(product.name?.nombre ?? '').trim();
  const unitPrice = isGift
    ? Number(product.price) || 0
    : Number(product.price?.precio) || 0;
  const quantity = Math.max(1, Number(product.quantity) || 1);
  if (!itemName) return null;
  return { item_id: itemId, item_name: itemName, price: unitPrice, quantity };
}

export function lineItemsFromCart(cart) {
  if (!Array.isArray(cart)) return [];
  return cart.map(lineItemFromCartProduct).filter(Boolean);
}

export function sumItemsValue(items) {
  return items.reduce((acc, row) => acc + Number(row.price) * Number(row.quantity), 0);
}

function purchaseLocalStorageKey(transactionId) {
  return LS_PURCHASE_RECORD_PREFIX + String(transactionId);
}

export function hasPurchaseBeenRecorded(transactionId) {
  if (typeof window === 'undefined' || transactionId == null || transactionId === '') return true;
  try {
    return window.localStorage.getItem(purchaseLocalStorageKey(transactionId)) === '1';
  } catch (e) {
    void e;
    return false;
  }
}

function recordPurchaseSent(transactionId) {
  if (typeof window === 'undefined' || transactionId == null || transactionId === '') return;
  try {
    window.localStorage.setItem(purchaseLocalStorageKey(transactionId), '1');
  } catch (e) {
    void e;
  }
}

const GATEWAY_STATUS_APPROVED = 'APPROVED';
const GATEWAY_STATUS_APROBADO = 'APROBADO';

export function isCardGatewayPaymentApproved(gatewayResponse) {
  if (!gatewayResponse) return false;
  const e = String(gatewayResponse.estado || '').toUpperCase();
  return e === GATEWAY_STATUS_APPROVED || e === GATEWAY_STATUS_APROBADO;
}

export function isBuyStatusRejectedForPurchase(data) {
  if (!data || data.estado == null) return false;
  const e = String(data.estado).toUpperCase().trim();
  const terminalReject = [
    'RECHAZADO',
    'RECHAZADA',
    'REJECTED',
    'DECLINED',
    'CANCELLED',
    'CANCELADO',
    'CANCELADA',
    'FALLIDO',
    'FALLIDA',
    'FAILED',
    'ERROR',
    'VOIDED',
  ];
  return terminalReject.includes(e);
}

export function isBuyStatusConfirmedForPurchase(data) {
  if (!data || data.estado == null) return false;
  const e = String(data.estado).toUpperCase().trim();
  if (isBuyStatusRejectedForPurchase(data)) return false;
  if (e === 'PENDIENTE' || e === 'PENDING') return false;
  const terminalOk = [
    'APPROVED',
    'APROBADO',
    'APROBADA',
    'PAGADO',
    'PAID',
    'EXITOSO',
    'COMPLETADO',
    'OK',
    'RECIBIDO',
    'CONFIRMADO',
  ];
  return terminalOk.includes(e);
}

export function isGatewayReturnApprovedFromUrlSearch(search) {
  if (!search) return false;
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(String(search));
  const keys = [
    'x_cod_transaction_state',
    'x_cod_response',
    'cod_respuesta',
    'x_response',
    'estado',
    'status',
  ];
  for (let i = 0; i < keys.length; i += 1) {
    const raw = params.get(keys[i]);
    if (raw == null || raw === '') continue;
    const value = String(raw).toUpperCase().trim();
    if (value === '1' || value === 'APPROVED' || value === 'APROBADO' || value === 'APROBADA' || value === 'OK') {
      return true;
    }
  }
  return false;
}

export function lineItemFromCatalogProduct(product, quantity) {
  if (!product || !product.plu) return null;
  const qty = Math.max(1, Number(quantity) || 1);
  const itemId = String(product.plu);
  const itemName = String(product.name?.nombre ?? '');
  const unitPrice = Number(product.price?.precio) || 0;
  if (!itemName) return null;
  return { item_id: itemId, item_name: itemName, price: unitPrice, quantity: qty };
}

export function lineItemFromProductDetail(product, gramaje, quantity) {
  if (!product || !product.plu) return null;
  const qty = Math.max(1, Number(quantity) || 1);
  let itemId = String(product.plu);
  let itemName = String(product.name?.nombre ?? '');
  let unitPrice = Number(product.price?.precio) || 0;
  if (gramaje && gramaje.id) {
    itemId = String(product.plu) + String(gramaje.id);
    unitPrice = Number(gramaje.price?.precio) || unitPrice;
    const gName = gramaje.nombre ? String(gramaje.nombre) : '';
    itemName = gName ? `${itemName} ${gName}`.trim() : itemName;
  }
  if (!itemName) return null;
  return { item_id: itemId, item_name: itemName, price: unitPrice, quantity: qty };
}

export function pushViewItem(currency, item) {
  if (!item) return;
  const items = normalizeItems([item]);
  const value = sumItemsValue(items);
  pushEcommerceEvent(EVENT_VIEW_ITEM, {
    currency,
    value,
    items,
  });
  return trackMetaViewContent(currency, value, items);
}

export function pushSelectItem(currency, item) {
  if (!item) return;
  const items = normalizeItems([item]);
  pushEcommerceEvent(EVENT_SELECT_ITEM, {
    currency,
    value: sumItemsValue(items),
    items,
  });
}

export function pushAddToCart(currency, productSnapshot) {
  const line = lineItemFromCartProduct(productSnapshot);
  if (!line) return;
  const items = normalizeItems([line]);
  const value = sumItemsValue(items);
  pushEcommerceEvent(EVENT_ADD_TO_CART, {
    currency,
    value,
    items,
  });
  return trackMetaAddToCart(currency, value, items);
}

export function pushViewCart(currency, cart) {
  const items = lineItemsFromCart(cart);
  if (!items.length) return;
  pushEcommerceEvent(EVENT_VIEW_CART, {
    currency,
    value: sumItemsValue(items),
    items,
  });
}

export function pushBeginCheckout(currency, cart) {
  const items = lineItemsFromCart(cart);
  if (!items.length) return undefined;
  const normalized = normalizeItems(items);
  const value = sumItemsValue(normalized);
  pushEcommerceEvent(EVENT_BEGIN_CHECKOUT, {
    currency,
    value,
    items: normalized,
  });
  const eventId = newMetaEventId();
  trackMetaInitiateCheckout(currency, value, normalized, eventId);
  return eventId;
}

export function pushAddPaymentInfo(currency, cart) {
  const items = lineItemsFromCart(cart);
  if (!items.length) return undefined;
  const normalized = normalizeItems(items);
  const value = sumItemsValue(normalized);
  pushEcommerceEvent(EVENT_ADD_PAYMENT_INFO, {
    currency,
    value,
    items: normalized,
  });
  const eventId = newMetaEventId();
  trackMetaAddPaymentInfo(currency, value, normalized, eventId);
  return eventId;
}

export function pushPurchaseConfirmedFromPayload(payload) {
  const tid = payload.transaction_id != null ? String(payload.transaction_id) : '';
  if (!tid || hasPurchaseBeenRecorded(tid)) return false;
  const items = normalizeItems(payload.items);
  if (!items.length) return false;
  const currency = normalizeCurrencyCode(payload.currency);
  const value = Number(payload.value);
  if (!Number.isFinite(value)) return false;
  pushEcommerceEvent(EVENT_PURCHASE, {
    currency,
    value,
    items,
    transaction_id: tid,
  });
  trackMetaPurchase(currency, value, items, tid);
  recordPurchaseSent(tid);
  return true;
}

export function pushPurchaseConfirmed(currency, transactionId, cart, purchaseValue) {
  const items = lineItemsFromCart(cart);
  if (!items.length) return false;
  return pushPurchaseConfirmedFromPayload({
    currency,
    transaction_id: transactionId,
    value: purchaseValue,
    items,
  });
}

function readPendingPurchaseRaw() {
  if (typeof window === 'undefined') return null;
  try {
    const fromLocal = window.localStorage.getItem(STORAGE_PENDING_PURCHASE);
    if (fromLocal) return fromLocal;
  } catch (e) {
    void e;
  }
  try {
    return window.sessionStorage.getItem(STORAGE_PENDING_PURCHASE);
  } catch (e) {
    void e;
    return null;
  }
}

function clearPendingPurchaseRaw() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_PENDING_PURCHASE);
  } catch (e) {
    void e;
  }
  try {
    window.sessionStorage.removeItem(STORAGE_PENDING_PURCHASE);
  } catch (e) {
    void e;
  }
}

export function stashPendingPurchaseForRedirect(payload) {
  if (typeof window === 'undefined' || !payload) return;
  const serialized = JSON.stringify(payload);
  try {
    window.localStorage.setItem(STORAGE_PENDING_PURCHASE, serialized);
  } catch (e) {
    void e;
  }
  try {
    window.sessionStorage.setItem(STORAGE_PENDING_PURCHASE, serialized);
  } catch (e) {
    void e;
  }
}

export function tryCompletePurchaseFromStash(transactionIdFromUrl) {
  if (typeof window === 'undefined' || !transactionIdFromUrl) return false;
  const raw = readPendingPurchaseRaw();
  if (!raw) return false;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    void e;
    return false;
  }
  if (!parsed || String(parsed.transaction_id) !== String(transactionIdFromUrl)) return false;
  clearPendingPurchaseRaw();
  return pushPurchaseConfirmedFromPayload(parsed);
}

export function completePurchaseFromGatewayReturn(transactionIdFromUrl, buyStatusData, urlSearch) {
  void urlSearch;
  if (!transactionIdFromUrl) return false;
  clearPendingPurchaseRaw();
  if (isBuyStatusRejectedForPurchase(buyStatusData)) return false;
  return true;
}
